import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import {
	json,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useRouteError,
} from '@remix-run/react';
import './tailwind.css'
import { useEffect } from 'react'
import type { LinksFunction } from "@remix-run/node";

let gtmScriptAdded = false

declare global {
	interface Window {
		[key: string]: object[]
	}
}

function addGtmScript(GTM_ID: string) {
	if (!GTM_ID || gtmScriptAdded) return

		; (function (w: Window, d: Document, s: 'script', l: string, i: string) {
			w[l] = w[l] || []
			w[l].push({
				'gtm.start': new Date().getTime(),
				event: 'gtm.js',
			})
			const f = d.getElementsByTagName(s)[0]
			const j = d.createElement<'script'>(s)
			const dl = l != 'dataLayer' ? '&l=' + l : ''
			j.async = true
			j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
			f.parentNode?.insertBefore(j, f)
		})(window, document, 'script', 'dataLayer', GTM_ID)

	gtmScriptAdded = true
}

export const loader = async () => {
	return json({
		GTM_ID: process.env.GTM_ID || '',
	})
}

export const links: LinksFunction = () => [
	// Favicon links
	{ rel: "icon", href: "/favicon.ico", sizes: "any" },
	{ rel: "icon", href: "/favicon-16x16.png", type: "image/png", sizes: "16x16" },
	{ rel: "icon", href: "/favicon-32x32.png", type: "image/png", sizes: "32x32" },
	{ rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
];

function Layout({ children }: { children: React.ReactNode }) {
	const data = useLoaderData<typeof loader>()
	const GTM_ID = data?.GTM_ID || ''

	useEffect(() => {
		if (GTM_ID) addGtmScript(GTM_ID)
	}, [GTM_ID])

	return (
		<html lang="en" className="h-full bg-white">
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Meta />
				<Links />
			</head>
			<body className="h-full">
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	)
}

function App() {
	return (
		<Layout>
			<Outlet />
		</Layout>
	)
}

export default withSentry(App);

export function ErrorBoundary() {
	const error = useRouteError(); captureRemixErrorBoundaryError(error);
	return (
		<html lang="en" className="h-full bg-white">
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Meta />
				<Links />
			</head>
			<body className="h-full">
				<Layout>
					<div className="flex min-h-full flex-col justify-center">
						<div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
							<h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
								Something went wrong
							</h1>
						</div>
					</div>
				</Layout>
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	)
}